import useModal from '@ctn/ui/components/ctn-modal/hooks/useModal';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import EntityGroup from '@/components/EntityGroup';
import IntervinientesListado from '@/components/IntervinientesListado';
import ModalInterviniente from '@/components/ModalInterviniente';
import { useGlobalContext } from '@/context/global.context';

import { notificationsConfig } from '@/helpers/notifications';
import { type ISujetoGroup, type ISujetoListado } from '@/interfaces/model/don.interface';
import { findID } from '@/utils/id';
import { getFullName } from '@/utils/string/string';

type IntervinienteGroupProps = {
	readonly className?: string;
	readonly name: string;
};

type Form = Record<string, ISujetoGroup>;

const IntervinienteGroup = ({ name, className }: IntervinienteGroupProps) => {
	const { openModal, isShowing, closeModal } = useModal({});
	const [editInterviente, setEditInterviente] = useState<ISujetoListado>();
	const {
		maestrosTramite: { tipoIntervencion },
	} = useGlobalContext();
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });
	const { control, watch, setValue } = useFormContext<Form>();

	const sujetoGroup = watch(name);
	const sujetos = sujetoGroup.items;
	const sujetosAdded = sujetos?.length ?? 0;

	const getMatchTipoIntervencion = (sujetoGroup: ISujetoGroup) =>
		tipoIntervencion.find(({ id }) => id === sujetoGroup.tipoIntervencion.id);

	const getMinRequireds = (sujetoGroup: ISujetoGroup) => {
		const minInterviniente = getMatchTipoIntervencion(sujetoGroup)?.minimoObligatorio ?? 0;
		return minInterviniente;
	};

	const onEdit = (data: ISujetoListado) => {
		setEditInterviente(data);
		openModal();
	};

	const onCloseIntervinienteNuevo = () => {
		closeModal();
		setEditInterviente(undefined);
	};

	const getCounterText = (sujetosAdded: number, minRequired: number) =>
		`Completados: ${sujetosAdded} / ${minRequired}`;

	const onSave = (value: ISujetoListado) => {
		const newValue = { ...sujetoGroup };
		const index = findID({ list: sujetoGroup.items, id: value.id, idReact: value.idReact });

		if (index === -1) {
			newValue.items.push(value);
		} else {
			newValue.items[index] = value;
		}

		setValue(name, newValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
	};

	const onDelete = (data: ISujetoListado) => {
		const index = findID({ list: sujetos, idReact: data.idReact, id: data.id });
		const { numeroDocumento, tipoIntervencion, nombre, apellido1RacSoc, apellido2 } = data;
		const fullName = getFullName(nombre, apellido1RacSoc, apellido2);

		const modalSuccesEliminarDocument = () => {
			openNotification(notificationTemplate.successMessage(
				'Interviniente eliminado',
				'Se ha eliminado correctamente al interviniente.'
			));
		};

		const remove = (indexToRemove: number) => {
			const newValue = { ...sujetoGroup };
			const newsItems = newValue.items.filter((_, index) => index !== indexToRemove);
			newValue.items = newsItems;
			setValue(name, newValue, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
		};

		openNotification(
			notificationTemplate.removeItem({
				title: 'Eliminar interviniente',
				content: `¿Eliminar ${tipoIntervencion.descripcion} - ${numeroDocumento} - ${fullName}?`,
				onAccept() {
					setTimeout(() => {
						modalSuccesEliminarDocument();
					}, 300);
					remove(index);
				},
			}),
		);
	};

	return (
		<>
			<Controller
				control={control}
				name={name}
				render={({ field: { value }, fieldState: { error } }) => (
					<EntityGroup
						title={sujetoGroup.tipoIntervencion.descripcion}
						action={{ label: 'Añadir interviniente', onClick: openModal }}
						hasError={Boolean(error?.message)}
						counterTxt={getCounterText(sujetosAdded, getMinRequireds(sujetoGroup))}
						className={className}
						isShowChildren={value.items.length > 0}
					>
						<IntervinientesListado data={value.items} onDelete={onDelete} onEdit={onEdit} />
					</EntityGroup>
				)}
			/>
			<ModalInterviniente
				isDisplayTipoIntervencion
				type="interviniente"
				isOpen={isShowing}
				data={editInterviente}
				filterIntervencion={[getMatchTipoIntervencion(sujetoGroup)?.id ?? 0]}
				onClose={onCloseIntervinienteNuevo}
				onSave={onSave}
			/>
		</>
	);
};

export default IntervinienteGroup;
